import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { CalendarEventResponse } from "../../../../../types/api";
import type { TournamentGamesResponse } from "../../../../../types/tournament-games";
import { getEvent, getEvents } from "../../../../../services/calendar";
import { getTournamentGames } from "../../../../../services/tournament-games";
import { useAuth } from "../../../../../hooks/useAuth";
import { useDocumentTitle } from "../../../../../hooks/useDocumentTitle";
import { formatEventRange } from "../../../../../utils/calendarTime";
import { Button } from "../../../../../components/Button";
import { Checkbox } from "../../../../../components/Checkbox";
import {
  buildPrintDays,
  gamePredicateFor,
  generatedLabel,
  parseSchedulePrintRequest,
  printCoachOptions,
  printFilterSummary,
  windowLabel,
  windowToIsoRange,
  writeCoachIds,
  type PrintDay,
  type PrintRow,
  type SchedulePrintRequest,
} from "./schedulePrint";
import "./SchedulePrintPage.css";

/** How many tournaments' games load at once, so a busy month does not fire twenty requests together. */
const GAMES_FETCH_CONCURRENCY = 4;

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      events: CalendarEventResponse[];
      gamesByEvent: Map<string, TournamentGamesResponse>;
    };

/**
 * Runs `fn` over `items`, at most `limit` at a time. Rejects on the first failure, and the other
 * workers stop taking new items then, so an outage does not keep firing requests nobody will use.
 */
const mapWithLimit = async <T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  let failed = false;
  const worker = async () => {
    while (!failed && next < items.length) {
      const index = next++;
      try {
        results[index] = await fn(items[index]);
      } catch (err) {
        failed = true;
        throw err;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

/**
 * Loads everything one printout needs: the events, then every tournament's games. All or nothing:
 * a printout that quietly drops one tournament's games looks complete on paper, which is worse
 * than a page that says it could not load.
 */
const loadPrintData = async (
  request: SchedulePrintRequest,
): Promise<{ events: CalendarEventResponse[]; gamesByEvent: Map<string, TournamentGamesResponse> }> => {
  let events: CalendarEventResponse[];
  if (request.kind === "event") {
    events = [await getEvent(request.eventId)];
  } else {
    const { fromIso, toIso } = windowToIsoRange(request.from, request.to);
    events = await getEvents(fromIso, toIso, request.scope);
  }
  const tournaments = events.filter((e) => e.eventType === "Tournament");
  const responses = await mapWithLimit(tournaments, GAMES_FETCH_CONCURRENCY, (e) =>
    getTournamentGames(e.id),
  );
  const gamesByEvent = new Map(tournaments.map((e, i) => [e.id, responses[i]]));
  return { events, gamesByEvent };
};

/** What identifies a load. The coach filter is applied locally, so it is left out. */
const loadKey = (request: SchedulePrintRequest | null): string => {
  if (!request) return "";
  return request.kind === "event"
    ? `event:${request.eventId}`
    : `range:${request.from}:${request.to}:${request.scope}`;
};

const GameCells = ({ row, showEvent }: { row: Extract<PrintRow, { kind: "game" }>; showEvent: boolean }) => (
  <>
    <td>
      <div>
        <span className="schedule-print__side">Home:</span> {row.home}
      </div>
      <div>
        <span className="schedule-print__side">Away:</span> {row.away}
      </div>
      {row.following && <div className="schedule-print__following">{row.following}</div>}
      {showEvent && <div className="schedule-print__sub">{row.eventTitle}</div>}
    </td>
    <td>
      {row.field && <div>{row.field}</div>}
      {row.address && <div className="schedule-print__sub">{row.address}</div>}
    </td>
  </>
);

const EventCells = ({ row }: { row: Extract<PrintRow, { kind: "event" }> }) => (
  <>
    <td>
      <div className="schedule-print__title">{row.title}</div>
      <div className="schedule-print__sub">{row.typeLabel}</div>
    </td>
    <td>
      {row.location && <div>{row.location}</div>}
      {row.address && <div className="schedule-print__sub">{row.address}</div>}
    </td>
  </>
);

const DayTable = ({
  day,
  showEvent,
  showPitchers,
}: {
  day: PrintDay;
  showEvent: boolean;
  showPitchers: boolean;
}) => (
  <section className="schedule-print__day" aria-labelledby={`print-day-${day.key}`}>
    <h2 id={`print-day-${day.key}`} className="schedule-print__day-title">
      {day.label}
    </h2>
    <table className="schedule-print__table">
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Game</th>
          <th scope="col">Field</th>
          <th scope="col">Coaches</th>
          {showPitchers && <th scope="col">Pitching</th>}
        </tr>
      </thead>
      <tbody>
        {day.rows.map((row) => (
          <tr key={`${row.kind}-${row.id}`} data-testid={`print-row-${row.id}`}>
            <td className="schedule-print__time">{row.time}</td>
            {row.kind === "game" ? (
              <GameCells row={row} showEvent={showEvent} />
            ) : (
              <EventCells row={row} />
            )}
            <td>{row.coaches.length > 0 ? row.coaches.join(", ") : "None assigned"}</td>
            {showPitchers && <td>{row.kind === "game" ? row.pitchers.join(", ") : ""}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

/**
 * The printable schedule (RPRR-82). Opened in a new tab from the schedule, the itinerary panel and
 * the event view, with their filters in the query string. It sits outside the dashboard layout, so
 * the page is the printout and nothing else; the toolbar above it is hidden when printing.
 *
 * The browser's own print dialog does the rest, and every current browser offers "Save as PDF"
 * there, which is why there is no PDF generator behind this.
 */
export const SchedulePrintPage = () => {
  const [params, setParams] = useSearchParams();
  const request = useMemo(() => parseSchedulePrintRequest(params), [params]);
  const { user, activeOrganization } = useAuth();
  const programName = activeOrganization?.name ?? user?.profile?.organizationName ?? "";
  const coachHeadingId = useId();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  // The tab title is also what the browser names the saved PDF, so it names the printout.
  useDocumentTitle(
    !request
      ? "Print schedule"
      : request.kind === "event"
        ? state.status === "ready"
          ? `${state.events[0]?.title ?? "Tournament"} schedule`
          : null
        : `Schedule ${windowLabel(request.from, request.to)}`,
  );
  const [attempt, setAttempt] = useState(0);
  // Fixed when the page opens, so a re-render does not change the printed time.
  const [generatedAt] = useState(() => new Date());

  const key = loadKey(request);
  useEffect(() => {
    if (!request) return;
    let active = true;
    setState({ status: "loading" });
    loadPrintData(request)
      .then((data) => {
        if (active) setState({ status: "ready", ...data });
      })
      .catch(() => {
        if (active) setState({ status: "error" });
      });
    return () => {
      active = false;
    };
    // Keyed on the load identity, not the request object: choosing coaches must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  // After "Try again" the button that had focus is gone; put focus on the printout instead.
  const mainRef = useRef<HTMLElement>(null);
  const focusAfterLoad = useRef(false);
  useEffect(() => {
    if (state.status !== "loading" && focusAfterLoad.current) {
      focusAfterLoad.current = false;
      mainRef.current?.focus();
    }
  }, [state.status]);

  const retry = useCallback(() => {
    focusAfterLoad.current = true;
    setAttempt((n) => n + 1);
  }, []);

  const setCoachIds = (next: readonly string[]) => {
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        writeCoachIds(updated, next);
        return updated;
      },
      { replace: true },
    );
  };

  const ready = state.status === "ready" ? state : null;
  const coachOptions = useMemo(
    () => (ready ? printCoachOptions(ready.events, ready.gamesByEvent) : []),
    [ready],
  );
  // Only coaches on this printout count. A bookmarked link can name someone with nothing here;
  // filtering by them would empty the page while no box is ticked and the header names nobody.
  const coachIds = useMemo(
    () =>
      (request?.coachIds ?? []).filter((id) => coachOptions.some((c) => c.userId === id)),
    [request, coachOptions],
  );

  const days = useMemo(() => {
    if (!ready || !request) return [];
    return buildPrintDays({
      events: ready.events,
      gamesByEvent: ready.gamesByEvent,
      predicate: gamePredicateFor(request, user?.profile?.id),
      coachIds,
      window: request.kind === "range" ? { from: request.from, to: request.to } : undefined,
    });
  }, [ready, request, coachIds, user?.profile?.id]);

  if (!request) {
    return (
      <main id="main-content" tabIndex={-1} className="schedule-print schedule-print--message">
        <h1>Nothing to print</h1>
        <p>This print link is missing its dates or event. Open the schedule and choose Print again.</p>
        <Link to="/schedule">Back to schedule</Link>
      </main>
    );
  }

  const event = request.kind === "event" ? ready?.events[0] : undefined;
  const title = request.kind === "event" ? (event?.title ?? "Tournament") : "Schedule";
  const subtitle =
    request.kind === "event"
      ? event
        ? [formatEventRange(event), event.locationName].filter(Boolean).join(" · ")
        : ""
      : windowLabel(request.from, request.to);
  const chosenNames = coachOptions
    .filter((c) => coachIds.includes(c.userId))
    .map((c) => c.displayName);
  const showPitchers = days.some((d) =>
    d.rows.some((r) => r.kind === "game" && r.pitchers.length > 0),
  );

  return (
    <div className="schedule-print">
      <div className="schedule-print__toolbar">
        <Link to="/schedule" className="schedule-print__back">
          Back to schedule
        </Link>
        {coachOptions.length > 0 && (
          <div
            className="schedule-print__coaches"
            role="group"
            aria-labelledby={coachHeadingId}
            data-testid="print-coach-filter"
          >
            <span id={coachHeadingId} className="schedule-print__coaches-label">
              Only games assigned to
            </span>
            {coachOptions.map((coach) => (
              <Checkbox
                key={coach.userId}
                label={coach.displayName}
                checked={coachIds.includes(coach.userId)}
                onChange={(e) =>
                  setCoachIds(
                    e.target.checked
                      ? [...coachIds, coach.userId]
                      : coachIds.filter((id) => id !== coach.userId),
                  )
                }
              />
            ))}
          </div>
        )}
        <div className="schedule-print__actions">
          <Button
            type="button"
            variant="primary"
            onClick={() => window.print()}
            disabled={!ready}
            data-testid="print-now"
          >
            Print / Save as PDF
          </Button>
          <span className="schedule-print__hint">
            To keep a copy, choose Save as PDF in the print window.
          </span>
        </div>
      </div>

      <main id="main-content" tabIndex={-1} className="schedule-print__sheet" ref={mainRef}>
        <header className="schedule-print__header">
          {programName && <p className="schedule-print__program">{programName}</p>}
          <h1 className="schedule-print__heading">{title}</h1>
          {subtitle && <p className="schedule-print__subtitle">{subtitle}</p>}
          <p className="schedule-print__meta" data-testid="print-filter-summary">
            {printFilterSummary(request, chosenNames)}
          </p>
          <p className="schedule-print__meta">{generatedLabel(generatedAt)}</p>
        </header>

        <div aria-live="polite">
          {state.status === "loading" && (
            <p className="schedule-print__status">Loading the schedule...</p>
          )}
          {state.status === "error" && (
            <div className="schedule-print__status" role="alert">
              Couldn't load the schedule, so there is nothing to print yet.{" "}
              <Button type="button" variant="secondary" onClick={retry}>
                Try again
              </Button>
            </div>
          )}
          {ready && days.length === 0 && (
            <p className="schedule-print__status" data-testid="print-empty">
              Nothing on the schedule matches these filters.
            </p>
          )}
        </div>

        {days.map((day) => (
          <DayTable
            key={day.key}
            day={day}
            showEvent={request.kind === "range"}
            showPitchers={showPitchers}
          />
        ))}
      </main>
    </div>
  );
};
